import { Box, Button, Center, Drawer, Modal, Paper, Stack, Text, TextInput } from '@mantine/core'
import clsx from 'clsx'
import { useState } from 'react'
import { useResponsiveModal } from '@/shared/lib/mantine'
import modalClasses from '@/shared/ui/component-styles/Modal.module.css'
import classes from './InitialsCaptureModal.module.css'

interface InitialsCaptureModalProps {
  opened: boolean
  onSubmit: (initials: string) => void
  /** True while the initials write is in flight; disables input + shows a loading Submit. */
  isSubmitting?: boolean
  /**
   * BD-8595: walkthrough-only view — same Drawer/Modal chrome as production,
   * with a disabled form and a spotlight target for Driver.js.
   */
  tutorialMode?: boolean
}

const MAX_INITIALS_LENGTH = 10

// The initials step is intentionally non-dismissible: it is shown once the order is
// already completed, so it can only be closed by submitting. Mantine requires an
// onClose handler, so we pass a no-op and disable every dismissal affordance.
function noop() {}

/**
 * Team member initials capture, shown after an order is completed.
 * Renders as a bottom sheet on mobile and a centered modal on desktop,
 * mirroring the verification-code flow.
 */
export function InitialsCaptureModal({
  opened,
  onSubmit,
  isSubmitting = false,
  tutorialMode = false,
}: InitialsCaptureModalProps) {
  const [initials, setInitials] = useState('')
  const { isSmUp, size, transitionProps } = useResponsiveModal()

  const trimmed = initials.trim()
  const isSubmitDisabled = tutorialMode || trimmed.length === 0 || isSubmitting

  function handleChange(value: string) {
    if (tutorialMode)
      return
    setInitials(value.toUpperCase())
  }

  function handleSubmit() {
    if (isSubmitDisabled)
      return
    // The field is intentionally not cleared: on success the provider removes
    // the pending entry and this modal unmounts; on failure it stays open with
    // the entered value so the team member can retry.
    onSubmit(trimmed)
  }

  const inputField = (
    <TextInput
      value={initials}
      onChange={event => handleChange(event.currentTarget.value)}
      placeholder="Please enter your initials"
      maxLength={MAX_INITIALS_LENGTH}
      autoFocus={!tutorialMode}
      disabled={isSubmitting || tutorialMode}
      size="md"
      w="100%"
      aria-label="Team member initials"
      data-testid="initials-input"
      onKeyDown={(event) => {
        if (event.key === 'Enter')
          handleSubmit()
      }}
    />
  )

  const submitButton = (
    <Button
      variant="filled"
      color="gray.8"
      size={isSmUp ? 'md' : 'lg'}
      radius="xl"
      fullWidth={!isSmUp}
      disabled={isSubmitDisabled}
      loading={!tutorialMode && isSubmitting}
      onClick={handleSubmit}
      data-testid="initials-submit"
    >
      Submit
    </Button>
  )

  // Mobile: bottom sheet drawer sitting over the dashboard.
  if (!isSmUp) {
    return (
      <Drawer
        opened={opened}
        onClose={noop}
        position="bottom"
        withCloseButton={false}
        closeOnClickOutside={false}
        closeOnEscape={false}
        radius="lg"
        padding="lg"
        transitionProps={transitionProps}
        styles={{ content: { height: 'auto', maxHeight: '90vh', overflow: 'hidden' } }}
        classNames={{
          header: classes.mobileDrawerHeader,
          content: classes.mobileDrawerContent,
        }}
        title={<span className={classes.mobileTitle}>Enter your initials</span>}
        // BD-8595 Step 14 mobile: spotlight the whole TMI sheet above the Driver.js mask.
        data-onboarding={tutorialMode ? 'tutorial-initials-modal' : undefined}
        zIndex={tutorialMode ? 10002 : undefined}
      >
        <Stack
          gap="lg"
          pt="xs"
          data-onboarding={tutorialMode ? 'tutorial-initials-form' : undefined}
        >
          {inputField}
          {submitButton}
        </Stack>
      </Drawer>
    )
  }

  // Desktop: centered modal with the gradient header.
  return (
    <Modal
      opened={opened}
      onClose={noop}
      withCloseButton={false}
      closeOnClickOutside={false}
      closeOnEscape={false}
      title={null}
      centered
      size={size}
      transitionProps={transitionProps}
      classNames={{
        content: modalClasses.modalContent,
        body: modalClasses.modalBody,
      }}
    >
      <div
        data-onboarding={tutorialMode ? 'tutorial-initials-form' : undefined}
      >
        <div className={clsx(modalClasses.modalHeader, modalClasses.desktopHeader)}>
          <h3 className={modalClasses.headerTitle}>Team member initials</h3>
        </div>

        <Box className={modalClasses.content}>
          <Center h="100%">
            <Paper radius="md" p="xl" bg="white" shadow="xs" w="100%">
              <Stack gap="lg" align="center">
                <Stack gap="xs" align="center">
                  <Text fw={700} size="xl" ta="center">
                    Enter your initials
                  </Text>
                  <Text c="gray.6" size="sm" ta="center">
                    Enter your initials to confirm delivery
                  </Text>
                </Stack>

                {inputField}
              </Stack>
            </Paper>
          </Center>
        </Box>

        <Box className={classes.actions}>
          {submitButton}
        </Box>
      </div>
    </Modal>
  )
}
